import React, { useEffect, useState } from 'react';
import { usePlugins } from './usePlugins';
import { usePluginRoutes } from './usePluginRoutes';
import { Plugin, RegisteredRoute } from './types';

/**
 * Comprehensive plugin debugging component for playground mode
 * Shows detailed information about plugin loading, registration, and route setup
 */
export const PluginLoadDebugger: React.FC = () => {
  const { plugins, isLoading } = usePlugins('all'); // Use 'all' to get all plugins
  const { routes, getRoutesByPlugin } = usePluginRoutes();
  const [logs, setLogs] = useState<string[]>([]);
  const [wsStatus, setWsStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');

  // Monitor WebSocket connection
  useEffect(() => {
    let ws: WebSocket | null = null;
    
    const connectWs = () => {
      try {
        setWsStatus('connecting');
        ws = new WebSocket('ws://localhost:11400');
        
        ws.onopen = () => {
          setWsStatus('connected');
          addLog('✅ WebSocket connected to playground server');
        };
        
        ws.onclose = () => {
          setWsStatus('disconnected');
          addLog('❌ WebSocket disconnected from playground server');
        };
        
        ws.onerror = (error) => {
          addLog('❌ WebSocket error: ' + error);
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            addLog(`📨 Received: ${data.type}${data.pluginName ? ` (${data.pluginName})` : ''}`);
          } catch (e) {
            addLog('📨 Received non-JSON message');
          }
        };
      } catch (error) {
        addLog('❌ Failed to connect to playground server: ' + error);
        setWsStatus('disconnected');
      }
    };

    const addLog = (message: string) => {
      const timestamp = new Date().toLocaleTimeString();
      setLogs(prev => [...prev.slice(-19), `${timestamp}: ${message}`]);
    };

    connectWs();

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  // Monitor plugin registration
  useEffect(() => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-19), `${timestamp}: Plugin registry updated - ${plugins.length} plugins`]);
  }, [plugins]);

  const clearLogs = () => setLogs([]);

  const debugPlugin = (pluginName: string) => {
    const plugin = plugins.find((p: Plugin) => p.meta.name === pluginName);
    if (!plugin) {
      alert(`Plugin "${pluginName}" not found`);
      return;
    }

    console.group(`🐛 Plugin Debug: ${pluginName}`);
    console.log('Plugin object:', plugin);
    console.log('Plugin meta:', plugin.meta);
    console.log('Plugin routes:', plugin.routes);
    console.log('Registered routes:', getRoutesByPlugin(pluginName));
    
    if (plugin.Component) {
      console.log('Main Component:', plugin.Component);
    }
    if (plugin.Components) {
      console.log('Named Components:', plugin.Components);
    }
    if (plugin.hooks) {
      console.log('Hooks:', Object.keys(plugin.hooks));
    }
    
    console.groupEnd();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return '#28a745';
      case 'connecting': return '#ffc107';
      case 'disconnected': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div style={{ 
      fontFamily: 'monospace', 
      border: '2px solid #007bff', 
      borderRadius: '8px', 
      padding: '20px', 
      margin: '20px',
      backgroundColor: '#f8f9fa'
    }}>
      <h2 style={{ margin: '0 0 20px 0', color: '#007bff' }}>
        🔌 Plugin Load Debugger
      </h2>

      {/* WebSocket Status */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '10px', 
        backgroundColor: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
      }}>
        <h3 style={{ margin: '0 0 10px 0' }}>WebSocket Status</h3>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px' 
        }}>
          <span style={{ 
            width: '12px', 
            height: '12px', 
            backgroundColor: getStatusColor(wsStatus),
            borderRadius: '50%'
          }}></span>
          <span>{wsStatus}</span>
          {wsStatus === 'connected' && <span>🟢 Ready to receive plugins</span>}
          {wsStatus === 'disconnected' && (
            <span style={{ color: '#dc3545' }}>
              ❌ Make sure playground server is running: <code>fps dev</code>
            </span>
          )}
        </div>
      </div>

      {/* Plugin Registry Status */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '10px', 
        backgroundColor: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
      }}>
        <h3 style={{ margin: '0 0 10px 0' }}>Plugin Registry</h3>
        <div>
          <strong>Loading:</strong> {isLoading ? '⏳ Yes' : '✅ No'} | 
          <strong> Plugins:</strong> {plugins.length} | 
          <strong> Routes:</strong> {routes.length}
        </div>
        
        {plugins.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <strong>Registered Plugins:</strong>
            <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
              {plugins.map((plugin: Plugin) => (
                <li key={plugin.meta.name} style={{ marginBottom: '5px' }}>
                  <span style={{ fontWeight: 'bold' }}>{plugin.meta.name}</span>
                  {plugin.meta.isPlayground && <span style={{ color: '#28a745' }}> [Playground]</span>}
                  {plugin.routes && (
                    <span style={{ color: '#6c757d' }}>
                      {' '}({Array.isArray(plugin.routes) ? plugin.routes.length : 'dynamic'} routes)
                    </span>
                  )}
                  <button 
                    onClick={() => debugPlugin(plugin.meta.name)}
                    style={{ 
                      marginLeft: '10px', 
                      padding: '2px 6px', 
                      fontSize: '11px',
                      cursor: 'pointer'
                    }}
                  >
                    Debug
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Route Registry */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '10px', 
        backgroundColor: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
      }}>
        <h3 style={{ margin: '0 0 10px 0' }}>Route Registry</h3>
        {routes.length === 0 ? (
          <div style={{ color: '#dc3545' }}>
            ❌ No routes registered. Check if your plugin exports routes correctly.
          </div>
        ) : (
          <div>
            {routes.map((route: RegisteredRoute) => (
              <div key={route.id} style={{ 
                padding: '8px', 
                marginBottom: '5px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '3px'
              }}>
                <div>
                  <strong>Path:</strong> {route.path} | 
                  <strong> Plugin:</strong> {route.pluginName} | 
                  <strong> Title:</strong> {route.title || 'N/A'}
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  Order: {route.order || 'N/A'} | 
                  Action: {route.action || 'N/A'} | 
                  Component: {route.component?.name || 'Anonymous'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Live Logs */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '10px', 
        backgroundColor: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '10px'
        }}>
          <h3 style={{ margin: 0 }}>Live Logs</h3>
          <button onClick={clearLogs} style={{ padding: '4px 8px', fontSize: '12px' }}>
            Clear
          </button>
        </div>
        <div style={{ 
          height: '150px', 
          overflow: 'auto', 
          backgroundColor: '#000',
          color: '#00ff00',
          padding: '10px',
          fontSize: '12px'
        }}>
          {logs.length === 0 ? (
            <div style={{ color: '#666' }}>Waiting for activity...</div>
          ) : (
            logs.map((log, index) => (
              <div key={index}>{log}</div>
            ))
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ 
        padding: '10px', 
        backgroundColor: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
      }}>
        <h3 style={{ margin: '0 0 10px 0' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            onClick={() => console.log('All plugins:', plugins)}
            style={{ padding: '8px 12px', fontSize: '12px' }}
          >
            Log All Plugins
          </button>
          <button 
            onClick={() => console.log('All routes:', routes)}
            style={{ padding: '8px 12px', fontSize: '12px' }}
          >
            Log All Routes
          </button>
          <button 
            onClick={() => {
              if (typeof window !== 'undefined') {
                console.log('window.registerPlugin:', window.registerPlugin);
                console.log('Available globals:', Object.keys(window).filter(k => k.includes('plugin') || k.includes('Plugin')));
              }
            }}
            style={{ padding: '8px 12px', fontSize: '12px' }}
          >
            Check Globals
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div style={{ 
        marginTop: '20px',
        padding: '15px',
        backgroundColor: '#d1ecf1',
        border: '1px solid #bee5eb',
        borderRadius: '4px',
        fontSize: '14px'
      }}>
        <h4 style={{ margin: '0 0 10px 0' }}>Debugging Steps:</h4>
        <ol style={{ margin: '0', paddingLeft: '20px' }}>
          <li>Make sure playground server is running and connected (green status above)</li>
          <li>Check that your plugin is listed in the Plugin Registry</li>
          <li>Verify that routes are showing up in the Route Registry</li>
          <li>Use the Debug button next to your plugin name to see detailed info</li>
          <li>Check browser console for any error messages</li>
          <li>Ensure your plugin exports default object with meta.name and routes</li>
        </ol>
      </div>
    </div>
  );
};